import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { eventHub } from '@atlassian/editor-plugin-ai/EventHub';

import { useEditorAIReplacingTitle } from '@confluence/editor-features/entry-points/useEditorAIReplacingTitle.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useDocumentPasteDetection } from './useDocumentPasteDetection.tsx';

export const TITLE_GENERATION_COMPLETE_TIMEOUT = 500;
export const AI_RESULT_VIEW_TIMEOUT = 30000;
export const AI_RESULT_VIEW_CHARACTER_THRESHOLD = 0.9;

type TitleGenerationI18n = {
	aiGeneratedTitleFromPaste: MessageDescriptor;
	aiGeneratedTitleFromPasteDescription: MessageDescriptor;
	aiGeneratedTitleDismissButton: MessageDescriptor;
};

export type UseTitleGenerationOnPasteProps = {
	textAreaRef: React.RefObject<HTMLTextAreaElement>;
	contentId: string;
	isLivePage: boolean;
	title?: string;
	isEditorEmpty?: boolean;
	userOffline?: boolean;
	i18n: TitleGenerationI18n;
	formatMessage: IntlShape['formatMessage'];
	flags?: FlagsStateContainer;
};

const getTargetEditorId = (
	textAreaRef: React.RefObject<HTMLTextAreaElement>,
): string | undefined => {
	const editors = Array.from(
		textAreaRef.current
			?.closest('.ak-editor-content-area')
			?.querySelectorAll<HTMLElement>('.ProseMirror') ?? [],
	).filter((editor) => !editor.closest('[data-editor-popup]'));

	return editors[0]?.dataset.editorId;
};

export const useTitleGenerationOnPaste = ({
	textAreaRef,
	isEditorEmpty,
	userOffline,
	contentId,
	isLivePage,
	title,
	flags,
	i18n,
	formatMessage,
}: UseTitleGenerationOnPasteProps) => {
	const [isGenerating, setIsGenerating] = useState(false);
	const generationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const aiResultViewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wasGeneratingRef = useRef(false);
	const originalGeneratedTitleLengthRef = useRef<number | null>(null);
	const currentTitleRef = useRef(title);
	const latestStreamedTitleRef = useRef<string>('');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { trackAIInteractionInit, trackAIResultView } = useAIEventsInstrumentation();

	const { pasteDetected, resetPasteDetected } = useDocumentPasteDetection(
		contentId,
		userOffline ?? false,
		isEditorEmpty ?? false,
	);

	useEffect(() => {
		currentTitleRef.current = title;
	}, [title]);

	useEffect(() => {
		return () => {
			if (generationTimeoutRef.current) {
				clearTimeout(generationTimeoutRef.current);
			}

			if (aiResultViewTimeoutRef.current) {
				clearTimeout(aiResultViewTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!pasteDetected) {
			return;
		}

		const targetEditorId = getTargetEditorId(textAreaRef);

		if (targetEditorId) {
			trackAIInteractionInit({
				attributes: {
					aiFeatureName: 'aiTitleGenOnPaste',
					doesNotMeetMAUCriteria: true,
				},
			});
			eventHub.publishToEditor({
				event: 'start prompt',
				data: {
					targetEditorId,
					adjustSelection: 'fullDocument',
					analyticSourceId: 'confluenceTitleGenOnPaste',
					prompt: 'suggest title',
				},
			});
		}

		resetPasteDetected();
	}, [resetPasteDetected, pasteDetected, textAreaRef, trackAIInteractionInit]);

	useEffect(() => {
		if (flags && isGenerating) {
			void flags.hideFlag('ai-generated-title-from-paste-flag');
			void flags.showInfoFlag({
				id: 'ai-generated-title-from-paste-flag',
				title: formatMessage(i18n.aiGeneratedTitleFromPaste),
				description: formatMessage(i18n.aiGeneratedTitleFromPasteDescription, {
					Link: (chunks: React.ReactNode[]) => (
						<Link target="_blank" href="https://www.atlassian.com/trust/atlassian-intelligence">
							{chunks}
						</Link>
					),
				}),
				actions: [
					{
						content: formatMessage(i18n.aiGeneratedTitleDismissButton),
						onClick: () => flags.hideFlag('ai-generated-title-from-paste-flag'),
					},
				],
			});
		}
	}, [isGenerating, formatMessage, flags, i18n]);

	useEffect(() => {
		if (wasGeneratingRef.current && !isGenerating) {
			const generatedTitleLength = latestStreamedTitleRef.current?.length ?? 0;
			originalGeneratedTitleLengthRef.current = generatedTitleLength;

			if (aiResultViewTimeoutRef.current) {
				clearTimeout(aiResultViewTimeoutRef.current);
			}

			aiResultViewTimeoutRef.current = setTimeout(() => {
				const currentTitleLength = currentTitleRef.current?.length ?? 0;
				const originalLength = originalGeneratedTitleLengthRef.current ?? 0;

				if (
					originalLength > 0 &&
					currentTitleLength >= originalLength * AI_RESULT_VIEW_CHARACTER_THRESHOLD
				) {
					trackAIResultView({
						attributes: {
							aiFeatureName: 'aiTitleGenOnPaste',
							doesNotMeetMAUCriteria: true,
						},
					});
				} else {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'deleted',
							actionSubject: 'aiTitleGenOnPaste',
							source: 'confluencePage',
							attributes: {
								contentId,
								isLivePage,
							},
						},
					}).fire();
				}

				originalGeneratedTitleLengthRef.current = null;
				aiResultViewTimeoutRef.current = null;
			}, AI_RESULT_VIEW_TIMEOUT);
		}

		wasGeneratingRef.current = isGenerating;
	}, [isGenerating, isLivePage, contentId, createAnalyticsEvent, trackAIResultView]);

	// Track streaming chunks to detect when generation completes
	const handleTitleChunk = useCallback((newTitle: string) => {
		latestStreamedTitleRef.current = newTitle;

		if (generationTimeoutRef.current) {
			clearTimeout(generationTimeoutRef.current);
		}
		setIsGenerating(true);

		generationTimeoutRef.current = setTimeout(() => {
			setIsGenerating(false);
			generationTimeoutRef.current = null;
		}, TITLE_GENERATION_COMPLETE_TIMEOUT);
	}, []);

	useEditorAIReplacingTitle(true, handleTitleChunk);
};
